import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import desktop from '../../assets/Sanctuary/Desktop - 5.png';
import frame10 from '../../assets/Sanctuary/Frame 10.png';
import frame7 from '../../assets/Sanctuary/Frame 7.png';
import frame8 from '../../assets/Sanctuary/Frame 8.png';
import frame4 from '../../assets/Sanctuary/Frame 4.png';
import { SanctuaryShell, sanctuaryLinks } from './SanctuaryShell';

const shots = [
  { src: frame10, alt: 'Sanctuary student dashboard' },
  { src: frame7, alt: 'Sanctuary manager tools' },
  { src: frame8, alt: 'Sanctuary attendance' },
  { src: frame4, alt: 'Sanctuary plans' },
];

const managerFeatures = [
  {
    title: 'Seats & desks',
    body: 'Assign desks, wings, and time slots in one view.',
  },
  {
    title: 'Student registration',
    body: 'Monthly or temporary members — without spreadsheets.',
  },
  {
    title: 'Renewals',
    body: 'See who’s active, expiring, or overdue at a glance.',
  },
  {
    title: 'Earnings',
    body: 'Track online and recorded payments more clearly.',
  },
  {
    title: 'Broadcasts',
    body: 'Send closures, timings, and fee reminders to students.',
  },
  {
    title: 'Razorpay',
    body: 'Collect fees via your library’s Razorpay — you stay the merchant.',
  },
];

const studentFeatures = [
  {
    title: 'Check-in',
    body: 'Mark attendance and keep a clear study-day record.',
  },
  {
    title: 'Focus & streaks',
    body: 'Session tracking and simple progress views.',
  },
  {
    title: 'Renew & pay',
    body: 'See membership status and renew when online pay is on.',
  },
  {
    title: 'Alerts',
    body: 'Renewals, library messages, and key updates.',
  },
];

const pricingPlans = [
  { name: 'Free trial', price: '30 days', detail: 'Full access to try the real workflow.', highlight: true },
  { name: 'Monthly', price: '₹299', detail: 'Flexible month-to-month.' },
  { name: 'Quarterly', price: '₹799', detail: 'Save versus paying monthly.' },
  { name: 'Yearly', price: '₹2,499', detail: 'Best value for year-round use.' },
];

function SanctuaryLanding() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  return (
    <main className="main sanctuary-page-root">
      <SanctuaryShell>
        <section className="sanc-hero">
          <div className="sanc-wrap sanc-hero__grid">
            <div>
              <p className="sanc-eyebrow">Library management · India</p>
              <h1 className="sanc-hero__brand">Sanctuary</h1>
              <p className="sanc-hero__line">The smarter way to manage your study space.</p>
              <p className="sanc-hero__desc">
                Seats, students, renewals, and fees — one Android app with Manager and Student
                portals.
              </p>
              <div className="sanc-hero__cta">
                <a
                  className="sanc-btn sanc-btn--primary"
                  href={sanctuaryLinks.play}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Start 30-day free trial
                </a>
                <a className="sanc-btn sanc-btn--ghost" href="#contact">
                  Contact us
                </a>
              </div>
              <p className="sanc-trust">Built for India · Razorpay-ready · Two roles in one app</p>
              <p className="sanc-company">
                Built by <strong>{sanctuaryLinks.business}</strong>
              </p>
              <p className="sanc-student-note">
                Students: get your Student ID from your library to sign in.
              </p>
            </div>
            <div className="sanc-hero__visual" aria-hidden="true">
              <img src={desktop} alt="" />
            </div>
          </div>
        </section>

        <section className="sanc-section" id="screens">
          <div className="sanc-wrap">
            <p className="sanc-kicker">Product tour</p>
            <h2 className="sanc-h2">See Sanctuary in action</h2>
            <p className="sanc-lead">
              Screens from the live app — <code>com.sahgal.sanctuaryy</code>.
            </p>
            <div className="sanc-shots">
              {shots.map((shot) => (
                <img key={shot.alt} src={shot.src} alt={shot.alt} />
              ))}
            </div>
          </div>
        </section>

        <section className="sanc-section sanc-section--tint" id="about">
          <div className="sanc-wrap">
            <p className="sanc-kicker">About</p>
            <h2 className="sanc-h2">Live ops, not spreadsheet chaos</h2>
            <p className="sanc-lead sanc-lead--wide">
              Sanctuary helps Indian study libraries run seats, students, and renewals in one
              place — instead of notebooks, WhatsApp, and Excel.
            </p>
            <div className="sanc-about-split">
              <article className="sanc-about-card">
                <h3>What it is</h3>
                <p>Ops + membership for study spaces: desks, students, attendance, and payments.</p>
              </article>
              <article className="sanc-about-card">
                <h3>What it isn’t</h3>
                <p>
                  Not a fee aggregator. Student fees go to the library’s Razorpay / bank — we don’t
                  hold that money.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="sanc-section" id="features">
          <div className="sanc-wrap">
            <p className="sanc-kicker">Features</p>
            <h2 className="sanc-h2">For managers &amp; students</h2>

            <h3 className="sanc-h3">Library managers</h3>
            <div className="sanc-points">
              {managerFeatures.map((item) => (
                <article className="sanc-point" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>

            <h3 className="sanc-h3 sanc-h3--spaced">Students</h3>
            <div className="sanc-points">
              {studentFeatures.map((item) => (
                <article className="sanc-point" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>

            <p className="sanc-note">
              <strong>Payments:</strong> Student fees → library’s Razorpay / bank. Sanctuary does
              not hold student money. Manager plans below are software fees for{' '}
              {sanctuaryLinks.business}.
            </p>
          </div>
        </section>

        <section className="sanc-section sanc-section--tint" id="how">
          <div className="sanc-wrap">
            <p className="sanc-kicker">How it works</p>
            <h2 className="sanc-h2">Simple paths</h2>
            <div className="sanc-paths">
              <div className="sanc-path">
                <h3 className="sanc-h3">Manager</h3>
                <ol className="sanc-steps">
                  <li>
                    <strong>Sign up</strong> on Play Store
                  </li>
                  <li>
                    <strong>Start</strong> 30-day free trial
                  </li>
                  <li>
                    <strong>Set up</strong> library, seats &amp; plans
                  </li>
                  <li>
                    <strong>Register</strong> students &amp; collect fees
                  </li>
                </ol>
              </div>
              <div className="sanc-path">
                <h3 className="sanc-h3">Student</h3>
                <ol className="sanc-steps">
                  <li>
                    <strong>Ask</strong> your library for a Student ID
                  </li>
                  <li>
                    <strong>Log in</strong> to the student portal
                  </li>
                  <li>
                    <strong>Check in</strong> and track sessions
                  </li>
                  <li>
                    <strong>Renew</strong> when online pay is enabled
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="sanc-section" id="pricing">
          <div className="sanc-wrap">
            <p className="sanc-kicker">Pricing</p>
            <h2 className="sanc-h2">Manager plans</h2>
            <p className="sanc-lead">
              Software for library owners. Student membership fees are set by each library — separate.
            </p>
            <div className="sanc-pricing">
              {pricingPlans.map((plan) => (
                <article
                  className={`sanc-price-card${plan.highlight ? ' sanc-price-card--hot' : ''}`}
                  key={plan.name}
                >
                  <p className="sanc-price-card__name">{plan.name}</p>
                  <p className="sanc-price-card__price">{plan.price}</p>
                  <p className="sanc-price-card__detail">{plan.detail}</p>
                </article>
              ))}
            </div>
            <p className="sanc-price-foot">
              Pro covers core manager tools (students, seats, plans). Prices in INR.
            </p>
          </div>
        </section>

        <section className="sanc-section sanc-section--tint" id="who">
          <div className="sanc-wrap">
            <p className="sanc-kicker">Audience</p>
            <h2 className="sanc-h2">Who it’s for</h2>
            <div className="sanc-who">
              <article className="sanc-who__card">
                <h3>Library owners</h3>
                <p>Study spaces that need seats, students, renewals, and fee tracking in one app.</p>
              </article>
              <article className="sanc-who__card">
                <h3>Students</h3>
                <p>Members who check in, see plan status, and renew when the library enables it.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="sanc-section" id="contact">
          <div className="sanc-wrap sanc-contact">
            <div>
              <p className="sanc-kicker">Contact</p>
              <h2 className="sanc-h2">Talk to us</h2>
              <p className="sanc-lead">Product, trial, privacy, or partnerships — write anytime.</p>
              <p className="sanc-about">
                <strong>{sanctuaryLinks.business}</strong> · Founder: <strong>Abhay Sahgal</strong>
              </p>
              <div className="sanc-hero__cta" style={{ marginTop: '1.25rem' }}>
                <a className="sanc-btn sanc-btn--primary" href={`mailto:${sanctuaryLinks.email}`}>
                  Email support
                </a>
                <a
                  className="sanc-btn sanc-btn--ghost"
                  href={sanctuaryLinks.play}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Play
                </a>
              </div>
            </div>

            <dl className="sanc-contact__card">
              <dt>Business</dt>
              <dd>{sanctuaryLinks.business}</dd>
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${sanctuaryLinks.email}`}>{sanctuaryLinks.email}</a>
              </dd>
              <dt>Phone</dt>
              <dd>
                <a href={sanctuaryLinks.phoneTel}>+91 {sanctuaryLinks.phone}</a>
              </dd>
              <dt>Address</dt>
              <dd>{sanctuaryLinks.address}</dd>
              <dt>Udyam</dt>
              <dd>{sanctuaryLinks.udyam}</dd>
              <dt>Legal</dt>
              <dd>
                <Link to="/projects/sanctuary/privacy">Privacy</Link>
                {' · '}
                <Link to="/projects/sanctuary/terms">Terms</Link>
                {' · '}
                <Link to="/projects/sanctuary/refund">Refund</Link>
              </dd>
            </dl>
          </div>
        </section>
      </SanctuaryShell>
    </main>
  );
}

export default SanctuaryLanding;
